import {
  EyeOutlined, PlusOutlined
} from '@ant-design/icons';
import {
  Alert,
  Badge, Button, Card,
  Space, Table,
  Tag
} from 'antd';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import React from 'react';

dayjs.extend(isSameOrAfter);

interface FollowUpActivitiesCardProps {
  lead: any;
  user: any;
  hasPermission: (permission: string) => boolean;
  followUps: any[];
  followUpStatuses: any[];
  isSalesUser: boolean;
  onOpenFollowUpModal: () => void;
  onOpenClosingRequestModal: (existingReq?: any) => void;
  onOpenClosingDetailModal: () => void;
  formatCurrency: (amount: number) => string;
  getFollowUpStatusColor: (status: string) => string;
}

export const FollowUpActivitiesCard: React.FC<FollowUpActivitiesCardProps> = ({
  lead,
  user,
  hasPermission,
  followUps,
  followUpStatuses,
  isSalesUser,
  onOpenFollowUpModal,
  onOpenClosingRequestModal,
  onOpenClosingDetailModal,
  formatCurrency,
  getFollowUpStatusColor,
}) => {
  const activeReq = lead?.active_closing_request;

  const getIsFollowUpDisabled = () => {
    if (followUps.length === 0 || followUpStatuses.length === 0) {
      return false;
    }

    const sortedFollowUps = [...followUps].sort((a, b) =>
      dayjs(b.created_at).diff(dayjs(a.created_at))
    );
    const latestFollowUp = sortedFollowUps[0];

    if (!latestFollowUp || !latestFollowUp.status_id) {
      return false;
    }

    const latestStatus = followUpStatuses.find(
      (status) => status.id === latestFollowUp.status_id
    );

    return latestStatus?.name.toLowerCase() === 'rsv';
  };

  const isFollowUpDisabled = getIsFollowUpDisabled();

  const sortedFollowUpsAsc = [...followUps].sort((a, b) => dayjs(a.created_at).diff(dayjs(b.created_at)));
  const raVisitEntry = sortedFollowUpsAsc.find(fu => fu.schedule_date);
  const riVisitEntry = sortedFollowUpsAsc.find(fu => fu.actual_date);

  const followUpColumns = [
    {
      title: 'Tanggal',
      dataIndex: 'created_at',
      key: 'date',
      render: (text: string) => dayjs(text).format('DD MMM YYYY, HH:mm'),
    },
    {
      title: 'Action',
      dataIndex: 'Action',
      key: 'action',
      render: (action: any) => action?.name || '-',
    },
    {
      title: 'Status',
      dataIndex: 'Status',
      key: 'status',
      render: (status: any) => (
        <Tag color={getFollowUpStatusColor(status?.name)}>
          {status?.name?.toUpperCase() || '-'}
        </Tag>
      ),
    },
    {
      title: 'Kategori',
      dataIndex: 'LeadFollowUpCategory',
      key: 'category',
      render: (category: any) => category?.name || '-',
    },
    {
      title: 'Respon',
      dataIndex: 'notes',
      key: 'notes',
    },
    {
      title: 'Rencana Visit',
      key: 'ra_visit',
      render: (_: any, record: any) => {
        if (raVisitEntry && dayjs(record.created_at).isSameOrAfter(dayjs(raVisitEntry.created_at))) {
          return dayjs(raVisitEntry.schedule_date).format('DD MMM YYYY');
        }
        return '-';
      },
    },
    {
      title: 'Realisasi Visit',
      key: 'ri_visit',
      render: (_: any, record: any) => {
        if (riVisitEntry && dayjs(record.created_at).isSameOrAfter(dayjs(riVisitEntry.created_at))) {
          return dayjs(riVisitEntry.actual_date).format('DD MMM YYYY');
        }
        return '-';
      },
    },
    {
      title: 'Oleh',
      dataIndex: 'User',
      key: 'user',
      render: (usr: any) => usr?.full_name || '-',
    },
  ];

  const renderClosingRequestBanner = () => {
    if (!activeReq) return null;

    if (activeReq.status === 'pending') {
      return (
        <Alert
          message="Pengajuan Closing OK Sedang Ditinjau ⌛"
          description={
            <div>
              <p>Pengajuan untuk pembeli <strong>{activeReq.buyer_name}</strong> senilai <strong>Rp {activeReq.omzet?.toLocaleString('id-ID')}</strong> telah dikirimkan pada {dayjs(activeReq.created_at).format('DD MMM YYYY, HH:mm')}.</p>
              <p>Sedang menunggu keputusan dari Atasan (Manager Kawasan).</p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16, borderRadius: 6 }}
        />
      );
    }

    if (activeReq.status === 'rejected') {
      return (
        <Alert
          message="Pengajuan Closing OK Ditolak ❌"
          description={
            <div>
              <p>Pengajuan closing ditolak oleh Atasan pada {activeReq.processed_at ? dayjs(activeReq.processed_at).format('DD MMM YYYY, HH:mm') : '-'}.</p>
              <p style={{ color: '#cf1322', fontWeight: 'bold' }}>Alasan Penolakan: {activeReq.rejection_reason}</p>
              <p>Silakan sesuaikan dokumen pendukung atau informasi pembeli, lalu klik <strong>Ajukan Ulang Closing</strong> untuk mengirimkan kembali.</p>
            </div>
          }
          type="error"
          showIcon
          style={{ marginBottom: 16, borderRadius: 6 }}
        />
      );
    }

    if (activeReq.status === 'approved') {
      return (
        <Alert
          message={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <span style={{ fontSize: '15px', fontWeight: 'bold', color: '#274f13' }}>
                TRANSAKSI CLOSING RESMI / O K 🎉
              </span>
              <Tag color="success" style={{ fontWeight: 600, margin: 0 }}>
                STATUS: O K (APPROVED)
              </Tag>
            </div>
          }
          description={
            <div style={{ marginTop: 8 }}>
              <p style={{ margin: '0 0 6px', color: '#262626' }}>
                Pengajuan closing telah disetujui untuk pembeli <strong>{activeReq.buyer_name}</strong> senilai{' '}
                <strong style={{ color: '#2e7d32', fontSize: '15px' }}>{formatCurrency(activeReq.omzet)}</strong> pada{' '}
                {activeReq.processed_at ? dayjs(activeReq.processed_at).format('DD MMM YYYY, HH:mm') : '-'}.
              </p>
              <p style={{ margin: '0 0 12px', color: '#595959' }}>
                Unit Properti: <strong>{activeReq.purchased_property_detail}</strong>
              </p>
              <Space wrap size={8}>
                <Button
                  type="primary"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={onOpenClosingDetailModal}
                  style={{ backgroundColor: '#7BAA18', borderColor: '#7BAA18' }}
                >
                  Lihat Detail Lengkap Data OK
                </Button>
                {/* {(activeReq.sp_document || lead.sp_document) && (
                  <Button
                    size="small"
                    icon={<FilePdfOutlined />}
                    href={activeReq.sp_document || lead.sp_document}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ borderColor: '#7BAA18', color: '#7BAA18' }}
                  >
                    Buka Dokumen SP (PDF)
                  </Button>
                )} */}
              </Space>
            </div>
          }
          type="success"
          showIcon
          style={{ marginBottom: 16, borderRadius: 8, border: '1px solid #b7eb8f', backgroundColor: '#f6ffed' }}
        />
      );
    }

    return null;
  };

  const renderClosingRequestButton = () => {
    const isAssignedSales = lead?.assigned_to?.id === user?.id;
    const isLeadRSV = lead?.lead_follow_up_status?.slug?.toLowerCase() === 'prospect';
    const hasRequestPermission = hasPermission('lead-closing.request');

    if (activeReq?.status === 'approved') {
      return (
        <Space size={8}>
          <Badge
            status="success"
            text={<span style={{ fontWeight: 'bold', color: '#7BAA18', fontSize: '14px' }}>CLOSED / O K 🎉</span>}
            style={{ padding: '4px 10px', border: '1px solid #7BAA18', backgroundColor: '#f6ffed', borderRadius: '4px' }}
          />
          {/* <Button
            type="primary"
            icon={<EyeOutlined />}
            style={{ backgroundColor: '#7BAA18', borderColor: '#7BAA18' }}
            onClick={onOpenClosingDetailModal}
          >
            Data Closing OK
          </Button> */}
        </Space>
      );
    }

    if (!isAssignedSales || !isLeadRSV || !hasRequestPermission) return null;

    if (!activeReq) {
      return (
        <Button
          style={{ backgroundColor: '#7BAA18', borderColor: '#7BAA18', color: '#fff', marginRight: 8 }}
          icon={<PlusOutlined />}
          onClick={() => onOpenClosingRequestModal()}
        >
          Ajukan Closing OK
        </Button>
      );
    }

    if (activeReq.status === 'pending') {
      return (
        <Button
          disabled
          style={{ backgroundColor: '#f5f5f5', color: 'rgba(0, 0, 0, 0.25)', borderColor: '#d9d9d9', marginRight: 8 }}
        >
          Menunggu Approval Atasan ⌛
        </Button>
      );
    }

    if (activeReq.status === 'rejected') {
      return (
        <Button
          type="primary"
          style={{ backgroundColor: '#fa8c16', borderColor: '#fa8c16', marginRight: 8 }}
          icon={<PlusOutlined />}
          onClick={() => onOpenClosingRequestModal(activeReq)}
        >
          Ajukan Ulang Closing 🔄
        </Button>
      );
    }

    return null;
  };

  return (
    <Card
      title="Follow Up Activities"
      extra={
        <Space>
          {renderClosingRequestButton()}
          {isSalesUser && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={onOpenFollowUpModal}
              disabled={isFollowUpDisabled}
              style={{ backgroundColor: '#7BAA18', borderColor: '#7BAA18' }}
            >
              Follow Up
            </Button>
          )}
        </Space>
      }
    >
      {renderClosingRequestBanner()}
      <Table
        dataSource={followUps}
        columns={followUpColumns}
        rowKey="id"
        pagination={{ pageSize: 5 }}
      />
    </Card>
  );
};

export default FollowUpActivitiesCard;
